<template>
    <div>
        <div  style="margin-bottom:7px;">
            Colored by {{category_name}}:
            <span v-for="category in categories_for_display" style="margin-right:4px;">
                <span
                        v-bind:style="{ borderBottom: category.color,padding:'3px', fontSize:'11px'}"
                        @mouseover="hover_category=category.category"
                        @mouseleave="hover_category=null">
                    {{category.category}}
                </span>
            </span>
        </div>
        <div ref="d3_root"></div>
        <div >
            <span style="color:lightgray">
                Treemap: {{labels.level1}}
                <span v-if="labels.level2">→ {{labels.level2}}</span>
                <span v-if="labels.level3">→ {{labels.level3}}</span>
            </span>
        </div>
    </div>
</template>

<script>
    import * as d3 from "d3";
    export default {
        data() {
            return {
                categories: [],
                categories_for_display: [],
                category_name: '',
                hover_category: null,
            }
        },
        mounted() {
            if (this.chartData && this.chartData.children) {
                this.draw()
            }
        },
        computed: {
            labels: function () {
                if (!this.chartData) {
                    return {
                        level1: '',
                        level2: '',
                        level3: ''
                    }
                } else {
                    let meta = this.chartData.meta
                    return {
                        level1: meta.level1,
                        level2: meta.level2,
                        level3: meta.level3
                    }
                }
            }
        },
        watch: {
            chartData: function (newValue) {
                if (newValue) this.draw()
            },
            height: function(newValue) {
                if (newValue) this.draw()
            },
            text_size: function(newValue) {
                if (newValue) this.draw()
            },
            hover_category: function(newValue) {
                this.draw()
            }
        },
        props: ['height','width', 'text_size', 'chartData'],
        methods: {
            draw() {
                this.categories_for_display = []
                this.chartData.children.forEach(child => {
                    this.categories[child.field_value] = child.color
                    let category_category = child.field_value
                    if (child.field_value===null) {
                        category_category = 'NO VALUE'
                    }
                    let category = {
                        category: category_category,
                        color: '3px solid ' + child.color
                    }
                    this.categories_for_display.push(category)
                })

                this.category_name = this.chartData.children[0].field_name

                const format = d3.format(",d")

                const svg_element = document.createElementNS("http://www.w3.org/2000/svg",'svg')

                const root = this.make_treemap(this.chartData);

                let text_height_in_px = 5 + (this.text_size / 100) * 15 // Want values between 5 and 20

                const svg = d3.select(svg_element)
                // https://medium.com/@louisemoxy/a-simple-way-to-make-d3-js-charts-svgs-responsive-7afb04bc2e4b
                    .attr("viewBox", `0 0 ${this.width} ${this.height}`)
                    .style("font", text_height_in_px + "px sans-serif");

                const leaf = svg.selectAll("g")
                    .data(root.leaves())
                    .enter().append("g")
                    .attr("transform", d => `translate(${d.x0},${d.y0})`);

                leaf.append("title")
                    .text(d => `${d.ancestors().reverse().map(d => d.data.field_value).join("/")}\n${format(d.value)}`);

                leaf.append("rect")
                    .attr("id", d => (d.leafUid = this.guid("leaf")))
                    .attr("fill", d => {
                        while (d.depth > 1) d = d.parent;
                        return d.data.color;
                    })
                    .attr("fill-opacity", d => {
                        while (d.depth > 1) d = d.parent;
                        if (!this.hover_category) {
                            return 0.6;
                        } else if (d.data.field_value==this.hover_category) {
                            return 0.85;
                        }else {
                            return 0.1;
                        }
                    })
                    .attr("width", d => d.x1 - d.x0)
                    .attr("height", d => d.y1 - d.y0)
                    .on("click", (d) => {
                        let node = d
                        let args = []
                        while (node.depth>=1) {
                            let raw_type = node.data.type
                            let massaged_type = raw_type
                            if (raw_type==='group_by') {
                                massaged_type = 'group_value'
                            } else if (raw_type==='primary_field') {
                                massaged_type = 'label'
                            } else {
                                massaged_type = 'color'
                            }
                            args[massaged_type] = node.data.field_value
                            node = node.parent
                        }
                        this.$emit('stackChartClicked', args);
                    })
                ;

                leaf.append("clipPath")
                    .attr("id", d => (d.clipUid = this.guid("clip")))
                    .append("use")
                    .attr("xlink:href", d => window.location.href + "#" + d.leafUid);

                leaf.append("g")
                    .attr("clip-path", d => "url(" + window.location.href + "#" + d.clipUid + ")") // need to wrap url() around this!
                    .selectAll("text")
                    .data((d) => {
                        let all = [];
                        let parent_text = d.parent ? d.parent.data.field_value : '';
                        let parent_opacity = 0.7
                        if (d.parent.data.field_value=="NO VALUE") {
                            parent_text = "no " + d.parent.data.field_name;
                            parent_opacity = 0.3
                        }
                        let parent_array = parent_text.toString().split(/[\s-_]/g);
                        parent_array.forEach(parent_word => {
                            let parent_obj = {};
                            parent_obj.type = "one-up-from-leaf";
                            parent_obj.value = parent_word;
                            parent_obj.opacity = parent_opacity;
                            parent_obj.font_weight = 600;
                            all.push(parent_obj)
                        })
                        let child_text = d.data.field_value
                        let child_opacity = 0.5
                        if (d.data.field_value=="NO VALUE") {
                            child_text = "no " + d.data.field_name;
                            child_opacity = 0.3
                        }
                        let child_array = child_text.toString().split(/[\s-_]/g);
                        child_array.forEach(child_word => {
                            let child_obj = {};
                            child_obj.type = "leaf";
                            child_obj.value = child_word;
                            child_obj.opacity = child_opacity;
                            child_obj.font_weight = 300;
                            all.push(child_obj)
                        });
                        let number_obj = {};
                        number_obj.type = "number";
                        number_obj.value = format(d.value)
                        number_obj.opacity = 0.7
                        number_obj.font_weight = 100
                        all.push(number_obj)
                        return(all);
                        // str = str.replace("NO VALUE","no~" + d.data.field_name) // "NO VALUE" is just way too prominent repeated in the treemap all over
                        // [
                        //  {
                        //    type: leaf,
                        //    value: posts,
                        //    opacity: 0.7,
                        //    font-weight: 200
                        //  },
                        //  {
                        //    type:one-up-from-leaf,
                        //    value: blog,
                        //    opacity: 0.7
                        //    font-weight: 500
                        //   }
                        // ]
                    })
                    .enter().append("text")
                    .attr("x", 3)
                    .attr("y", (d, i, nodes) => `${(i === nodes.length - 1) * 0.3 + 1.1 + i * 0.9}em`)
                    .attr("fill-opacity", (d, i, nodes) => {
                        let width = $(nodes[i]).parent().parent().find('rect')[0].width.baseVal.value
                        if (width<10) return 0
                        // if (d.startsWith("no~")) return 0.3
                        return d.opacity
                    })
                    .attr("font-weight",(d) => { return d.font_weight

                    })
                    .text(d => d.value);

                while (this.$refs.d3_root.firstChild) {
                    this.$refs.d3_root.removeChild(this.$refs.d3_root.firstChild);
                }

                this.$refs.d3_root.append(svg.node())

            },
            make_treemap(data) {
                return d3.treemap()
                    .size([this.width, this.height])
                    .padding(1)
                    .round(true)
                    (d3.hierarchy(data)
                        .sum(d => d.count)
                        .sort((a, b) => b.height - a.height || b.value - a.value))
            },
            guid(base) {
                function s4() {
                    return Math.floor((1 + Math.random()) * 0x10000)
                        .toString(16)
                        .substring(1);
                }
                return base + "-" + s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
            },
            removeElement(elementId) {
                // Removes an element from the document
                var element = document.getElementById(elementId);
                element.parentNode.removeChild(element);
            }
        }
    }
</script>